import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {BotOptionDTO, ConversationChannel, TranscriptAnalyticsRequest, TranscriptAnalyticsResponse} from "../../../api/generated";
import {botApi, RequestStatus, transcriptApi} from "../../../api/apiConfiguration";
import {RootState} from "../../../app/store";
import moment from "moment";
import {SelectOption} from "../../elements/select/SelectDropdown";

export type TranscriptAnalyticsRequestPartial = Partial<TranscriptAnalyticsRequest>;

interface AnalyticsState {
    botOptions: SelectOption<string>[];
    botOptionsRequestStatus: RequestStatus;
    analyticsResponse?: TranscriptAnalyticsResponse;
    analyticsRequestStatus: RequestStatus;
    analyticsRequest: TranscriptAnalyticsRequest;
}

export function getBrowserUtcOffset(): string {
    const totalMinutes = -new Date().getTimezoneOffset();
    const sign = totalMinutes >= 0 ? "+" : "-";
    const absoluteMinutes = Math.abs(totalMinutes);
    const hours = Math.floor(absoluteMinutes / 60).toString().padStart(2, "0");
    const minutes = (absoluteMinutes % 60).toString().padStart(2, "0");
    return `${sign}${hours}:${minutes}`;
}

const getDefaultRequest = (): TranscriptAnalyticsRequest => ({
    startDate: moment().subtract(6, "days").toISOString(),
    endDate: moment().toISOString(),
    publicBotIdList: [],
    conversationChannelList: [],
    clientUtcOffset: getBrowserUtcOffset(),
});

const initialState: AnalyticsState = {
    botOptions: [],
    botOptionsRequestStatus: RequestStatus.NONE,
    analyticsResponse: undefined,
    analyticsRequestStatus: RequestStatus.NONE,
    analyticsRequest: getDefaultRequest(),
};

export const fetchTranscriptAnalytics = createAsyncThunk<TranscriptAnalyticsResponse, undefined, { state: RootState }>(
    "analytics/fetchTranscriptAnalytics",
    (_payload, {getState}) => {
        const request = getState().analytics.analyticsRequest;
        return transcriptApi.getTranscriptAnalytics({
            transcriptAnalyticsRequest: {
                ...request,
                clientUtcOffset: getBrowserUtcOffset()
            },
        });
    }
);

export const fetchAnalyticsBotOptions = createAsyncThunk<Array<BotOptionDTO>, undefined>(
    "analytics/fetchAnalyticsBotOptions",
    () => {
        return botApi.getBotOptions();
    }
);

export const analyticsSlice = createSlice({
    name: "analytics",
    initialState,
    reducers: {
        updateAnalyticsRequest: (state, {payload}: PayloadAction<TranscriptAnalyticsRequestPartial>) => {
            state.analyticsRequest = {
                ...state.analyticsRequest,
                ...payload,
            };
        },
        resetAnalyticsRequest: (state) => {
            state.analyticsRequest = getDefaultRequest();
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchTranscriptAnalytics.pending, (state) => {
            state.analyticsRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchTranscriptAnalytics.fulfilled, (state, {payload}) => {
            state.analyticsResponse = payload;
            state.analyticsRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchTranscriptAnalytics.rejected, (state) => {
            state.analyticsRequestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(fetchAnalyticsBotOptions.fulfilled, (state, {payload}) => {
            state.botOptions = payload.map(option => ({
                value: option.publicId || "",
                label: option.siteName || "Unknown Assistant"
            }));
            state.botOptionsRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchAnalyticsBotOptions.pending, (state) => {
            state.botOptionsRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchAnalyticsBotOptions.rejected, (state) => {
            state.botOptionsRequestStatus = RequestStatus.REJECTED;
        });
    }
});

export const {
    updateAnalyticsRequest,
    resetAnalyticsRequest,
} = analyticsSlice.actions;

export const analyticsRequestSelector = (state: RootState): TranscriptAnalyticsRequest => state.analytics.analyticsRequest;
export const analyticsResponseSelector = (state: RootState): TranscriptAnalyticsResponse | undefined => state.analytics.analyticsResponse;
export const analyticsRequestStatusSelector = (state: RootState): RequestStatus => state.analytics.analyticsRequestStatus;
export const analyticsBotOptionsSelector = (state: RootState): SelectOption<string>[] => state.analytics.botOptions;
export const analyticsBotOptionsRequestStatusSelector = (state: RootState): RequestStatus => state.analytics.botOptionsRequestStatus;

export const analyticsChannelOptions: SelectOption<ConversationChannel>[] = [
    {value: ConversationChannel.Widget, label: "Widget"},
    {value: ConversationChannel.ConfidentAi, label: "Confident AI"},
    {value: ConversationChannel.SuperchatWhatsApp, label: "Superchat WhatsApp"},
    {value: ConversationChannel.SuperchatEmail, label: "Superchat Email"},
    {value: ConversationChannel.SuperchatSms, label: "Superchat SMS"},
    {value: ConversationChannel.SuperchatTelegram, label: "Superchat Telegram"},
    {value: ConversationChannel.SuperchatInstagram, label: "Superchat Instagram"},
    {value: ConversationChannel.SuperchatFacebook, label: "Superchat Facebook"},
    {value: ConversationChannel.ContactCenterWhatsApp, label: "Contact Center WhatsApp"},
    {value: ConversationChannel.ContactCenterEmail, label: "Contact Center Email"},
    {value: ConversationChannel.ContactCenterSms, label: "Contact Center SMS"},
];

export default analyticsSlice.reducer;
